import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Box,
  Divider,
} from '@mui/material';

import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { ApiToken } from 'services/api-access.service';
import { Button } from 'components/Button';

interface DeleteTokenConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  token?: ApiToken;
  userName?: string;
  deleteAll?: boolean;
  loading?: boolean;
}

export function DeleteTokenConfirmationDialog({
  open,
  onClose,
  onConfirm,
  token = undefined,
  userName = '',
  deleteAll = false,
  loading = false,
}: DeleteTokenConfirmationDialogProps) {
  const expiresOn = token?.expiresAt ? format(new Date(token.expiresAt), 'dd/MM/yyyy HH:mm') : 'N/A';
  const generatedOn = token?.createdAt ? format(new Date(token.createdAt), 'dd/MM/yyyy HH:mm') : 'N/A';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: 'rounded-none w-[400px] max-w-[400px]',
      }}
    >
      <DialogTitle className="relative py-4 px-4 border-b border-gray-200 flex items-center">
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          <FormattedMessage
            id={
              deleteAll
                ? 'api_access.user_tokens.delete_all_dialog_title'
                : 'api_access.user_tokens.delete_one_dialog_title'
            }
            defaultMessage={deleteAll ? 'Delete all Tokens for user {userName}?' : 'Delete Token?'}
            values={{ userName }}
          />
        </Typography>
      </DialogTitle>
      <DialogContent className="my-15 px-4">
        <DialogContentText className="text-primary text-sm leading-relaxed">
          <FormattedMessage
            id="api_access.user_tokens.delete_dialog.permanent_action"
            defaultMessage="This action is permanent and cannot be undone."
          />
          {!deleteAll && token && (
            <FormattedMessage
              id="api_access.user_tokens.delete_dialog.token_lost_message"
              defaultMessage=" The following Token will be lost:"
            />
          )}
        </DialogContentText>
        {!deleteAll && token && (
          <Box className="mb-4">
            <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem', color: 'primary.main' }}>
              <strong>
                <FormattedMessage id="api_access.user_tokens.table.refresh_token_id" />:
              </strong>{' '}
              {token.id}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem', color: 'primary.main' }}>
              <strong>
                <FormattedMessage id="api_access.user_tokens.table.generated_on" />:
              </strong>{' '}
              {generatedOn}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'primary.main' }}>
              <strong>
                <FormattedMessage id="api_access.user_tokens.table.expires_on" />:
              </strong>{' '}
              {expiresOn}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <Divider className="border-gray-200" />
      <DialogActions className="px-4 py-3 gap-0 justify-end">
        <Button onClick={onClose} priority="secondary" disabled={loading} className="min-w-[100px] normal-case mr-2">
          <FormattedMessage id="general.button.cancel" />
        </Button>
        {loading ? (
          <Box className="min-w-[140px] flex items-center justify-center py-1.5 px-3">
            <LoadingSpinner size="small" />
          </Box>
        ) : (
          <Button onClick={onConfirm} variant="critical" className="min-w-[140px]">
            <FormattedMessage
              id={deleteAll ? 'api_access.user_tokens.delete_all_button' : 'api_access.user_tokens.delete_button'}
            />
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
